<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { FileSignature, X, Download, CheckCircle2, XCircle, ShieldAlert, Calendar, User } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';
  import { api } from '../stores/api.js';
  import SignatureStatePanel from './SignatureStatePanel.svelte';
  import SignatureStateBadge from './SignatureStateBadge.svelte';
  import { signatureStateOf } from './signatureState.js';

  export let proposalId = null;
  export let proposalNumber = '';
  export let isCustomerUser = false;

  const dispatch = createEventDispatcher();

  let loading = true;
  let error = null;
  let signatureInfo = null;

  // Use customer portal API for customers, internal API for admins
  $: apiBasePath = isCustomerUser ? '/customer-portal/proposals' : '/proposals';

  onMount(async () => {
    await loadSignatureInfo();
  });

  async function loadSignatureInfo() {
    loading = true;
    error = null;
    try {
      console.log(`[SignatureInfoModal] Loading signature info for proposal ${proposalId} (isCustomer: ${isCustomerUser})`);
      const response = await api.get(`${apiBasePath}/${proposalId}/signature-info`);
      signatureInfo = response;
      console.log(`[SignatureInfoModal] Loaded signature info:`, signatureInfo);
    } catch (err) {
      console.error('[SignatureInfoModal] Error loading signature info:', err);
      error = err.message || $_('signatureInfo.loadError');
    } finally {
      loading = false;
    }
  }

  async function downloadSignedPdf(type) {
    try {
      console.log(`[SignatureInfoModal] Downloading ${type}-signed document for proposal ${proposalId}`);
      const response = await api.getBlob(`${apiBasePath}/${proposalId}/signed-pdf/${type}`);

      // Get file type from signature info to determine extension
      const info = type === 'customer' ? signatureInfo?.customer : signatureInfo?.final;
      const fileType = info?.fileType || 'pdf';
      const extension = fileType === 'p7m' ? 'p7m' : 'pdf';

      // Create download link
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      const suffix = type === 'customer' ? 'firmato_cliente' : 'firmato_finale';
      a.download = `proposta_${proposalNumber || proposalId}_${suffix}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[SignatureInfoModal] Error downloading signed document:', err);
      alert(err.message || $_('signatureInfo.downloadError'));
    }
  }

  function handleClose() {
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      handleClose();
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  }

  /**
   * What is actually known about one signer's entry.
   *
   * The old label said "Valida" whenever the CMS envelope parsed, which is the
   * finding being fixed: a structurally intact signature whose certificate
   * chain was never verified is not a valid digital signature, it is an
   * unverified one. Three answers, never two.
   *
   * @param {{valid?: boolean, chainVerified?: boolean}} sig One entry of `signatures`
   * @returns {'verified'|'structural'|'invalid'}
   */
  function signerVerdict(sig) {
    if (sig?.chainVerified === true) return 'verified';
    if (sig?.valid === true || sig?.structurallyValid === true) return 'structural';
    return 'invalid';
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 z-50 flex items-center justify-center">
  <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={handleClose}></div>

  <!-- Modal -->
  <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 scale-in">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent rounded-t-2xl">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
          <FileSignature class="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">{$_('signatureInfo.title')}</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatureInfo.subtitle')} {proposalNumber || proposalId}</p>
        </div>
      </div>
      <button
        on:click={handleClose}
        class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
      >
        <X size={20} />
      </button>
    </div>

    <!-- Content -->
    <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
      {#if loading}
        <div class="flex items-center justify-center py-8">
          <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      {:else if error}
        <div class="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      {:else}
        <div class="space-y-6">
          <!-- Customer Signature Section -->
          <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div class="flex items-center justify-between mb-4">
              <!-- No green tick merely because a file exists: the badge says
                   which of the three states this document is actually in. -->
              <h3 class="font-semibold text-gray-900 dark:text-white flex items-center flex-wrap gap-2">
                <FileSignature class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                {$_('signatureInfo.customerSignature')}
                {#if signatureInfo?.hasCustomerSigned}
                  <SignatureStateBadge state={signatureStateOf(signatureInfo?.customer)} compact={true} />
                {/if}
              </h3>
              {#if signatureInfo?.hasCustomerSigned}
                <button
                  on:click={() => downloadSignedPdf('customer')}
                  class="btn btn-sm btn-secondary flex items-center"
                >
                  <Download class="h-4 w-4 mr-1" />
                  {$_('signatureInfo.download')}
                </button>
              {/if}
            </div>

            {#if signatureInfo?.hasCustomerSigned && signatureInfo?.customer}
              <div class="space-y-3 text-sm">
                <!-- What is actually known about this document, before any
                     signer detail: state first, so nothing below can be read
                     as a verification that never happened. -->
                <SignatureStatePanel info={signatureInfo.customer} showTechnicalDetail={!isCustomerUser} />
                {#if signatureInfo.customer.uploadedAt}
                  <div class="flex items-center text-gray-600 dark:text-gray-300">
                    <Calendar class="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                    {$_('signatureInfo.uploadedAt')}: {formatDate(signatureInfo.customer.uploadedAt)}
                  </div>
                {/if}
                {#if signatureInfo.customer.signatures && signatureInfo.customer.signatures.length > 0}
                  {#each signatureInfo.customer.signatures as sig}
                    <div class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <div class="flex items-center text-gray-700 dark:text-gray-200">
                        <User class="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">{sig.signer || sig.signerDN || $_('signatureInfo.unknownSigner')}</span>
                      </div>
                      {#if sig.signingTime}
                        <div class="text-gray-500 dark:text-gray-400 text-xs mt-1 ml-6">
                          {sig.signingTime}
                        </div>
                      {/if}
                      <div class="mt-1 ml-6">
                        {#if signerVerdict(sig) === 'verified'}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                            <CheckCircle2 class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.chainVerified')}
                          </span>
                        {:else if signerVerdict(sig) === 'structural'}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300">
                            <ShieldAlert class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.structuralOnly')}
                          </span>
                        {:else}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
                            <XCircle class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.invalid')}
                          </span>
                        {/if}
                      </div>
                    </div>
                  {/each}
                {/if}
              </div>
            {:else}
              <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatureInfo.noCustomerSignature')}</p>
            {/if}
          </div>

          <!-- Final Signature Section -->
          <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-semibold text-gray-900 dark:text-white flex items-center flex-wrap gap-2">
                <FileSignature class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                {$_('signatureInfo.finalSignature')}
                {#if signatureInfo?.hasFinalSigned}
                  <SignatureStateBadge state={signatureStateOf(signatureInfo?.final)} compact={true} />
                {/if}
              </h3>
              {#if signatureInfo?.hasFinalSigned}
                <button
                  on:click={() => downloadSignedPdf('final')}
                  class="btn btn-sm btn-secondary flex items-center"
                >
                  <Download class="h-4 w-4 mr-1" />
                  {$_('signatureInfo.download')}
                </button>
              {/if}
            </div>

            {#if signatureInfo?.hasFinalSigned && signatureInfo?.final}
              <div class="space-y-3 text-sm">
                <SignatureStatePanel info={signatureInfo.final} showTechnicalDetail={!isCustomerUser} />
                {#if signatureInfo.final.uploadedAt}
                  <div class="flex items-center text-gray-600 dark:text-gray-300">
                    <Calendar class="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                    {$_('signatureInfo.uploadedAt')}: {formatDate(signatureInfo.final.uploadedAt)}
                  </div>
                {/if}
                {#if signatureInfo.final.signatures && signatureInfo.final.signatures.length > 0}
                  {#each signatureInfo.final.signatures as sig}
                    <div class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <div class="flex items-center text-gray-700 dark:text-gray-200">
                        <User class="h-4 w-4 mr-2 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">{sig.signer || sig.signerDN || $_('signatureInfo.unknownSigner')}</span>
                      </div>
                      {#if sig.signingTime}
                        <div class="text-gray-500 dark:text-gray-400 text-xs mt-1 ml-6">
                          {sig.signingTime}
                        </div>
                      {/if}
                      <div class="mt-1 ml-6">
                        {#if signerVerdict(sig) === 'verified'}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                            <CheckCircle2 class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.chainVerified')}
                          </span>
                        {:else if signerVerdict(sig) === 'structural'}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300">
                            <ShieldAlert class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.structuralOnly')}
                          </span>
                        {:else}
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
                            <XCircle class="h-3 w-3 mr-1" />
                            {$_('signatureInfo.invalid')}
                          </span>
                        {/if}
                      </div>
                    </div>
                  {/each}
                {/if}
              </div>
            {:else}
              <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatureInfo.noFinalSignature')}</p>
            {/if}
          </div>
        </div>
      {/if}
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end rounded-b-2xl">
      <button
        type="button"
        on:click={handleClose}
        class="btn btn-secondary"
      >
        {$_('common.close')}
      </button>
    </div>
  </div>
</div>
